/* eslint-disable no-use-before-define */
import React, { CSSProperties } from 'react';
import { Agent, Project } from '@moderepo/mode-internal-apis';
import { Grid, Paper } from '@material-ui/core';
import { GenericMessageBox } from '@moderepo/mode-app-components';
import { TFunction } from 'i18next';
import { FileRequestAction } from '@moderepo/mode-apis';
import { RitzComponentsCollection } from './ritzComponentsCollection';
import { getSpecificProjectCustomUIConfig, isGridContainerConfig, isRitzComponentConfig, isRitzDrawerMainMenuItem }
    from './ritzUIExtensionConfigValidation';
import { EntityClassUISettings, TableViewHiddenFields } from '../containers';
import { EntitiesListingViewType } from '../components';



/**
 * The name of All the menu items that are supported by Ritz. Each menu item will have a name so that project custom UI config can set the order
 * of the menu just by providing the array of menu item names in the order that they want the menu to appear.
 */
export enum RitzMenuItemName {
    DASHBOARD = 'Dashboard',
    HOMES = 'Homes',
    USERS = 'Users',
    DEVICES = 'Devices',
    DOWNLOADS = 'Downloads',
    ADMIN = 'Admin',
    ENTITIES = 'Entities',
    ALERT_RULES = 'AlertRules',
    ALERTS = 'Alerts',
    MANAGE_DATA = 'ManageData',
    VIEW_DATA = 'ViewData',
}


/**
 * The setting interface for Device Registration Wizard
 */
export interface DeviceRegistrationWizardSettings {
    // Each project can have more than 1 device classes that can be configured using the wizard. For each device class that can be configured
    // using the wizard, the admin will need to specify the deviceClass, the entityClass to be instantiated with the device, and the
    // template for the device config JSON.
    readonly deviceClassesSettings: {
        readonly deviceClass: string;
        readonly entityClass: string;
        readonly deviceConfigTemplate: {};
        // OPTIONAL file name to use for the device config download file INCLUDING the extension. The DEFAULT filename will be "ug.json"
        readonly deviceConfigFileName?: string | undefined;
    }[];

    // To OPTIONALLY enable the traditional add device option so that we can have a way to add device without entity. This can be useful
    // for test projects that have many device classes but only some device classes require entities. This option allow us to add devices
    // the old way.
    readonly allowAddDeviceWithoutEntity?: boolean | undefined;
}


export interface EntitiesTableViewAttributesSettings {
    // To show only the specific list of attributes
    readonly includeAttributes?: readonly string[] | undefined;

    // To show all attributes EXCEPT the specified list of attributes
    readonly excludeAttributes?: readonly string[] | undefined;

    // To add custom settings per attribute
    readonly settingsPerAttribute?: {
        readonly [attrKey: string]: {
            readonly decimalPlaces?: number | undefined;
            readonly unitKey?: string | undefined;
        } | undefined;
    } | undefined;
}


export interface EntitiesTableViewDescendantCountsSettings {
    // To show only the specific list of descendantCounts
    readonly includeDescendantCounts?: readonly string[] | undefined;
}


/**
 * The setting interface to customize the entities table view.
 */
export interface EntitiesTableViewSettings {
    // The Entities Table view contains many columns. The admin can use this map to hide some columns. By default only the "name", "description",
    // "Entity Class", and "Image" columns are shown. The admin can have the table view show the other columns by specifically set the column
    // to "false". And to hide one of the default visible columns, the admin can specifically set the value to true. For example
    //      hiddenFields: {
    //          description: true,      // to hide the description column
    //          icon: false,            // Because "icon" is hidden by default, we need to specifically set it to false to show the icon column
    //      }
    readonly hiddenFields?: TableViewHiddenFields | undefined;

    // The specify the ordering of the table columns. This allows the admin to change the order the columns are displayed. The admin DOES NOT
    // need to specify the order of all the columns, only the columns that they want to show first. For example, to have the "image" and "icon"
    // columns show first and then use the default order for the rest of the columns, the admin can set this attribute to this value
    //      fieldsOrder: ["image", "icon"]
    readonly fieldsOrder?: readonly string[] | undefined;

    // To have the table component show entities' attributes. If this is NOT undefined then it will be treated as TRUE and the table component
    // will display attributes.
    readonly showAttributesSettings?: EntitiesTableViewAttributesSettings | undefined;

    // To have the table component show entities' descendantCounts.
    readonly showDescendantCountsSettings?: EntitiesTableViewDescendantCountsSettings | undefined;
}


/**
 * To customize the Entities Listing page's UI.
 */
export interface EntitiesListingViewSettings {
    // To override the default viewType of the Entities Listing page. For "dataView", the default viewType is Table and for the "manageView",
    // the default viewType is "Tree"
    readonly defaultViewType?: EntitiesListingViewType | undefined;

    // Whether or not to enable the options to change the view type. By default, the UI only show the view options for "ManageView". To enable this
    // feature for "DataView", the admin can set this setting to "true" for the "DataView". Or to hide this option for "ManageView", the admin can set
    // this setting to "false" for "ManageView"
    readonly enableChangeViewOptions?: boolean | undefined;

    // Whether or not to enable the "Search" entities option. By default, the UI only show the search option for "ManageView". To enable this feature
    // for "DataView", the admin can set this setting to "true" for the "DataView". Or to hide this option for "ManageView", the admin can set
    // this setting to "false" for "ManageView"
    readonly enableSearchOptions?: boolean | undefined;


    // TODO - Add customization for each specific viewType e.g. Table, Grid, Tree, List
    readonly tableView?: EntitiesTableViewSettings | undefined;

    /**
     * To customize the table view per Entity Classes. This will override the "tableView" settings which is a global settings. For example,
     * let's say we want the table to display all robot's serial number when the table view shows robots entities, and show "address" attribute
     * when the user view all "customer" entities, we can configure it like this
     * {
     *      tableViewPerEntityClasses: [{
     *          entityClasses: ["ruber_3000", "sweeper_1", "karakuri_1000"],
     *          settings: {
     *              showAttributesSettings: {
     *                  includeAttributes: ["serial_number"]
     *              },
     *              showDescendantCountsSettings: {
     *                  includeDescendantCounts: ["nozzle", "feeder"]
     *              }
     *          }
     *      }, {
     *          entityClasses: ["customer"],
     *          settings: {
     *              showAttributesSettings: {
     *                  includeAttributes: ["address"]
     *              },
     *              showDescendantCountsSettings: {
     *                  includeDescendantCounts: ["line"]
     *              }
     *          }
     *      }]
     * }
     */
    readonly tableViewPerEntityClasses?: readonly [{
        // The custom table view settings to use
        readonly settings: EntitiesTableViewSettings;

        // The list of entity classes that the settings should apply to
        readonly entityClasses: readonly string[];
    }] | undefined;
}


/**
 * To customize the Entities feature's "manageView" and "dataView". This allows the admin to customize these view's default UI. For example,
 * by default, the Entities Listing page in "manageView" use the "Tree" view to list the entities. We can have the project use a different view
 * for example, "Grid" view.
 */
export interface EntitiesViewsSettings {
    readonly manageView?: EntitiesListingViewSettings | undefined;
    readonly dataView?: EntitiesListingViewSettings | undefined;
}


/**
 * The main setting interface for the Entities feature. This allows the admin to customize the Entities feature's UI that are not configurable
 * from Entity classes' uiSettings.
 */
export interface EntitiesFeatureSettings {
    // To customize the "manageView" and "dataView"
    readonly viewsSettings?: EntitiesViewsSettings | undefined;
}



/**
 * The interface for configuring which feature/s to enable for each project. This is only used for configuring SPECIAL features which
 * we want to be able to enabled/disabled per project.
 */
export interface RitzFeaturesConfig {

    /**
     * A feature that a project can enable is the 'fileTransfer' feature (but only for RC projects only for now). To enable this feature,
     * the project.uiSettings must contain an object with the key 'fileTransfer' and must provide other settings for this features.
     *
     * Example 1: This project will enable the fileTransfer features BUT only allow download from gateway and download from robot actions.
     * The user can't upload file to gateway nor robots.
     *
     *      uiSettings: {
     *          drawerMenu: ...,
     *          enabledFeatures: {
     *              fileTransfer: {
     *                  control_server_to_cloud: {},
     *                  robot_to_cloud: {}
     *              }
     *          }
     *      }
     *
     * Example 2: This project has fileTransfer feature enabled and the only action is allowed is download/upload from/to gateways but no
     * download/upload from/to robots.
     *      uiSettings: {
     *          drawerMenu: ...,
     *          enabledFeatures: {
     *              fileTransfer: {
     *                  control_server_to_cloud: {},
     *                  cloud_to_control_server: {}
     *              }
     *          }
     *      }
     */
    readonly fileTransfer?: {
        readonly supportedActions: {
            readonly [FileRequestAction.DOWNLOAD_FROM_DEVICE]?: {
            } | undefined;
            readonly [FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER]?: {
            } | undefined;
            readonly [FileRequestAction.DOWNLOAD_FROM_ROBOT]?: {
            } | undefined;
            readonly [FileRequestAction.UPLOAD_TO_DEVICE]?: {
            } | undefined;
            readonly [FileRequestAction.UPLOAD_TO_DEVICE]?: {
            } | undefined;
            readonly [FileRequestAction.UPLOAD_TO_CONTROL_SERVER]?: {
            } | undefined;
            readonly [FileRequestAction.UPLOAD_TO_ROBOT]?: {
            } | undefined;
        };

        /**
         * The name of the attribute where the config is located in the GatewayConfig. Log FileTypes/Directory are defined in the
         * GatewayConfig because each project will have different set of file types/directories where the log files are stored.
         * Frontend will need to read this config and get the list of file types so that it can show the options the user to choose.
         * All project will store this config under GatewayConfig's 'fileTransfer' attribute. However, some project like AGV will have this
         * config under a different attribute, 'agvFileTransfer'. So this setting allow us to have the UI looks for the file type config in
         * a different place for cases a project has different gateway settings. This is OPTIONAL and only required if a project does not use
         * 'fileTransfer' attribute name.
         */
        readonly gatewayFileTransferConfigAttrName?: string | undefined;
    } | undefined;


    /**
     * A config to enable the 'Device Registration Wizard'. This is OPTIONAL but if a project wants to enable this feature, the admin
     * must provide the required configuration
     */
    readonly deviceRegistrationWizard?: DeviceRegistrationWizardSettings | undefined;


    // OPTIONS to show/hide the TSDB and Videos tabs. These 2 tabs are visible by default so to hide them, set these flags to false.
    // If a project needs to show these 2 tabs then don't need to set these flags.
    readonly tsdbTabVisible?: boolean | undefined;
    readonly videosTabVisible: boolean | undefined;

    // OPTION to enable Entities feature. Entities feature will be disabled initially and only enabled for projects with this flag turned on
    readonly entities?: boolean | EntitiesFeatureSettings | undefined;

    // OPTION to enable the alerting feature. Alerting feature will be disabled initially and only enabled for projects with this flag turned on
    readonly alerting?: boolean | undefined;

    // Some projects don't support userless home e.g. robot cloud projects. These projects don't have a UI for the agent to add home for
    // a user therefore we can't allow the agent to create userless home first and then assign home to user afterward. The agent will need
    // to create home and assign to a user in the same flow.
    // The default is FALSE because this is MODE platform feature so it is enabled. Only for project that needs to disable this feature
    // will need to set this flag to TRUE.
    readonly disableUserlessHome?: boolean | undefined;
}


/**
 * The interface for a drawer menu item config
 */
export interface RitzDrawerMenuItem {
    readonly itemName: string;
    readonly labelKey: string;
    readonly iconKey: string | JSX.Element;
    readonly link?: string | undefined;
    readonly disabled?: boolean;
    readonly hidden?: boolean;
    readonly adminOnly?: boolean;
}



/**
 * For menu items shown in the drawer menu is actually a type of this interface which is exactly as the RitzDrawerMenuItem except
 * these menu items can have sub menus. This is to support multiple levels of menu.
 */
export interface RitzDrawerMainMenuItem extends RitzDrawerMenuItem {
    // The OPTIONAL list of sub menu items that this menu item has.
    readonly subMenuItems?: readonly RitzDrawerMenuItem[];

    // By default, if a main menu item has submenu, it will not be selectable. Clicking on the item will only expand/collapse the menu,
    // but won't trigger the 'onMenuItemClicked' handler. Set this to true to make it selectable. If this is set to true, the DrawerMenu's
    // "collapsible" should be set to false so that clicking on the menu doesn't collapse it. Or else it will do both select the menu item
    /// and collapse which would be weird behavior.
    // If a main menu DOES NOT have submenu then it will automatically be selectable, this attribute is ignored.
    readonly selectable?: boolean | undefined;
}


/**
 * These are the config for the Drawer Menu
 */
export interface DrawerMenuConfig {
    readonly backgroundImage?: string | undefined;
    readonly appNameKey: string;
    readonly projectLogo: string | JSX.Element;
    readonly menuItems: readonly RitzDrawerMainMenuItem [];

    // Whether or not to automatically expand all menu items that has submenu. The default is TRUE unless this is specifically set to false
    readonly expandAllOnLoad?: boolean | undefined;

    // Whether or not to allow the user to collapse menu items that has submenu. The default is TRUE unless this is specifically set to false
    readonly collapsible?: boolean | undefined;
}


/**
 * These are the customizable config for the drawer menu per project. It is similar to the DrawerMenuConfig but not exactly the same.
 * It only have a list of props that a project can override based on the DrawerMenuConfig's set of props.
 */
export interface CustomDrawerMenuConfig extends Pick<
DrawerMenuConfig, 'appNameKey' | 'projectLogo' | 'backgroundImage' | 'expandAllOnLoad' | 'collapsible'
> {
    readonly menuItems?: readonly Partial<RitzDrawerMainMenuItem> [] | undefined;
}



export interface RitzComponentConfig {
    readonly componentName: string;
    readonly compProps?: {[attrName: string]: any} | undefined;

    /**
     * Props that come from route params e.g. projectId, userId, homeId, etc... from this route /projects/123/users/456/homes/789
     * For this props, the values usually can't be an actual value because we don't know the value for these props, they can be
     * any value depending on the project, user, home, devices, etc... that the user is looking at.. So instead if specifying an
     * exact value, use placeholders e.g.
     *      {
     *          componentName: "UserHomeContainer",     // The container that display user Home info
     *          routeParamProps: {
     *              projectId: {{projectId}},
     *              userId: {{userId}}
     *              ...
     *          }
     *      }
     * The keys "projectId", "userId", needs to match the name of the component's props. The value {{projectId}}, {{userId}} needs to match
     * the route param name. The page that display this component will take the route params values and replace these place holder with the
     * actual value and pass them to the component. So based on this route /projects/123/users/456/homes/789 and the config in the example,
     * the page will create this component <UserHomeContainer projectId={123} userId={456} homeId={789} />
     * NOTE: it is important for the person configuring the component to know what props the component needs and what page this component
     * is added to. The component might need a prop that does not exist in the URL param. For example, we can't add this component
     * UserHomeContainer to this page /projects/1234. This page is the project root page and at this point, we only have the projectId.
     * We can't show UserHomeContainer because we don't have the userId and homeId yet.
     */
    readonly routeParamProps: {[name: string]: any | undefined}
}

export interface GridItemConfig {
    readonly compProps?: {[attrName: string]: any} | undefined;
    readonly components: readonly (GridContainerConfig | RitzComponentConfig)[];
}

export interface GridContainerConfig {
    readonly compProps?: {[attrName: string]: any} | undefined;
    readonly gridItems: readonly GridItemConfig[];
}


/**
 * These are the config for a page in Ritz.
 */
export interface RitzPageConfig {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    // The page's top title/subtitle. The page has a default title/subtitle so we only need to provide these values if we want to override the default
    readonly titleKey?: string | undefined;
    readonly subtitleKey?: string | undefined;
    readonly components?: readonly (GridContainerConfig | RitzComponentConfig)[];
}



/**
 * A ProjectUIConfig is a config that defines what features a project have. The UI will be built dynamically based on this config.
 * This allows us to create custom UI for different PROJECT TYPE which apply to all projects that have a specific type.
 */
export interface ProjectUIConfig {

    readonly drawerMenu: DrawerMenuConfig;

    /**
     * To override the project's landing page to one of the existing pages instead of the default landing page /projects/{{projectId}}.
     * This allow the admin to change the project's landing page to the Homes Listing page e.g. /projects/{{projectId}}/homes or the
     * Users listing page e.g. /projects/{{projectId}}/users. NOTE, the value of the landingPage can contain the "{{projectId}}" placeHolder.
     * The system will replace the {{projectId}} with the actual projectId.
     */
    readonly landingPage?: string | undefined;

    /**
     * Each type of project has a different set of IMPORTANT things it needs to show. Some project type focuses on customer support so
     * it needs to display a UI for customer lookup as the main UI while some project type focuses on Monitoring Issues therefore it
     * main UI needs to have a quick way to view issues. The projectDashboardPage is the First/Main page that an agent would land on
     * so it needs to show what the agent wants to see the most. Since each project is going to want to see different thing therefore,
     * the ProjectDashboardPage will need to be customizable, per Project Type and per specific project.
     */
    readonly projectDashboardPage: RitzPageConfig;

    /**
     * Ritz uses User feature to represent Customers. Users are more like end-user so this doesn't work well for some project. For some
     * project like AGV, their customers are companies, therefore the Users view in Ritz doesn't work well for AGV. So we need to change
     * the Users view to make it look more like Companies but we don't want to do this for ALL projects, we only need to do this for some
     * project. We don't have a concept of Company right now and we might add this concept in the future. So for now, we will temporary
     * add this OPTIONAL flag to any project that wants to change the Users view to look more like companies. Once we have a real
     * Company concept, we will change this.
     */
    readonly enableCompanyView?: boolean | undefined;

    /**
     * The set of SPECIAL features that the project enabled and the config for those features
     */
    readonly enabledFeatures?: RitzFeaturesConfig | undefined;


    /**
     * Ritz has a default uiSettings for all entity classes. All entities will show Children KPI components, followed by KPI Metrics, and
     * followed by Ranking table. We can override the default uiSettings for each entity class if we provide 'uiSettings' in entity class'
     * displaySettings. However, we will have to do it per entity class. If we want to override the Ritz' default uiSettings for ALL entity
     * classes, we can do it with this project UI Config. This uiSettings will apply to all entity classes EXCEPT for entity classes that
     * have their own uiSettings. Entity classes can still have their own uiSettings which will override this. Here is the uiSettings
     * in the order of HIGHEST precedence
     * EntityClass's uiSettings, Project's entityClassUISettings, Ritz entityClassUISettings.
     */
    readonly entityClassUISettings?: EntityClassUISettings | undefined;
}


/**
 * ProjectCustomUIConfig is a config to override the ProjectUIConfig. Therefore, ProjectCustomUIConfig has a similar structure to the
 * ProjectUIConfig. It contains a subset of props from ProjectUIConfig that are customizable.
 * This interface is used for allowing each SPECIFIC project to override the PROJECT TYPE config. This mean we can customize the config
 * by PROJECT ID.
 */
export interface ProjectCustomUIConfig {
    readonly landingPage?: string | undefined;
    readonly drawerMenu?: CustomDrawerMenuConfig | undefined;
    readonly projectDashboardPage?: RitzPageConfig | undefined;
    readonly enableCompanyView?: boolean | undefined;
    readonly enabledFeatures?: RitzFeaturesConfig | undefined;
    readonly entityClassUISettings?: EntityClassUISettings | undefined;
}


/**
 * Given an array of customMenuItems which contains only partial menu item objects, not the full object, return an array of full menu item
 * objects by copying the objects from the defaultMenuItems. For example:
 *
 * Input:
 *      defaultMenuItems = [{
 *          name: "home", icon: "home", label: "Homes", link: "/homes"
 *      }, {
 *          name: "users", icon: "office", label: "Customers", link: "/users"
 *      }, {
 *          name: "admin", icon: "settings", label: "Admin", link: "/admin"
 *      }]
 *
 *      customMenuItems: [{
 *          name: "users"                                    // set the Users menu to show first (all attributes will be copied from defaultMenuItems)
 *      }, {
 *          name: "homes"                                    // and then the Homes menu (all attributes will be copied from defaultMenuItems)
 *      }, {
 *          name: "robots", icon: "robot", label: "Robots", link: "/robots"  // Add this menu item (NOTE: this does not exist in the defaultMenuItems)
 *      }, {
 *          name: "admin", icon: "chart", label: "Settings"     // and then the "Admin" menu BUT rename the menu to "Settings" and use icon "chart"
 *      }, ]
 *
 *  Output: NOTE: The order of the menu items will be based on the order of the items in the "customMenuItems" array
 *      [{
 *          name: "users", icon: "office", label: "Customers", link: "/users"
 *      }, {
 *          name: "home", icon: "home", label: "Homes", link: "/homes"
 *      }, {
 *          name: "robots", icon: "robot", label: "Robots", link: "/robots"
 *      }, {
 *          name: "admin", icon: "chart", label: "Settings", link: "/admin"
 *      }]
 */
const mergeMenuItems = (
    defaultMenuItems: readonly RitzDrawerMainMenuItem[] | undefined,
    customMenuItems: readonly Partial<RitzDrawerMainMenuItem>[],
): RitzDrawerMainMenuItem[] => {

    const resultMenuItems: RitzDrawerMainMenuItem[] = [];

    customMenuItems.forEach((customMenuItem) => {

        // Find the item in the defaultMenuItems that has the same name as the customMenuItem
        const defaultMenuItem = defaultMenuItems?.find((defaultMenuItem) => {
            return defaultMenuItem.itemName === customMenuItem.itemName;
        });

        if (defaultMenuItem) {
            // If there is an existing menuItem defined in "defaultMenuItems" that has the same name as the "customMenuItem" then we will
            // combine the attributes from these 2 object and add the combined object into the result.

            // Merge the defaultMenuItem with the customMenuItem. If both objects have value for the same prop, the value defined in customMenuItem
            // will override the value in defaultMenuItem
            const mergedMenuItem = {
                ...defaultMenuItem,
                ...customMenuItem,
                // Merge the subMenuItems if both object has subMenuItems
                subMenuItems: customMenuItem.subMenuItems
                    ? mergeMenuItems(defaultMenuItem.subMenuItems, customMenuItem.subMenuItems) : undefined,
            };

            resultMenuItems.push(mergedMenuItem);
        } else if (isRitzDrawerMainMenuItem(customMenuItem)) {
            // If there isn't a menuItem exist in "defaultMenuItems" that matches the "customMenuItem" BUT if the "customMenuItem" contain all the
            // required attributes then we will include this "customMenuItem" in the result as well.
            resultMenuItems.push(customMenuItem);
        }
    });

    return resultMenuItems;
};


/**
 * Given a DrawerMenuConfig and a CustomDrawerMenuConfig, merge the customDrawerMenuConfig into the drawerMenuConfig to override
 * the props in drawerMenuConfig.
 * @param drawerMenuConfig
 * @param customDrawerMenuConfig
 */
export const mergeDrawerMenuConfig = (
    drawerMenuConfig: DrawerMenuConfig,
    customDrawerMenuConfig: CustomDrawerMenuConfig | undefined,
): DrawerMenuConfig => {
    if (customDrawerMenuConfig) {

        // Return a new DrawerMenuConfig object by replacing the props from drawerMenuConfig with the new props from customDrawerMenuConfig
        return {
            // make a copy of the drawerMenuConfig
            ...drawerMenuConfig,

            // override the appNameKey if the customDrawerMenuConfig contains a value for appNameKey
            appNameKey: customDrawerMenuConfig.appNameKey ?? drawerMenuConfig.appNameKey,

            // override the projectLogo if the customDrawerMenuConfig contains a value for projectLogo
            projectLogo: customDrawerMenuConfig.projectLogo ?? drawerMenuConfig.projectLogo,

            // override the background if the customDrawerMenuConfig contains a value for backgroundImage
            backgroundImage: customDrawerMenuConfig.backgroundImage || drawerMenuConfig.backgroundImage,

            // Override the menuItems.
            menuItems: customDrawerMenuConfig.menuItems
                ? mergeMenuItems(drawerMenuConfig.menuItems, customDrawerMenuConfig.menuItems)
                : drawerMenuConfig.menuItems,

            // override the expandAllOnLoad if the customDrawerMenuConfig contains a value for expandAllOnLoad
            expandAllOnLoad: customDrawerMenuConfig.expandAllOnLoad ?? drawerMenuConfig.expandAllOnLoad,

            // override the collapsible if the customDrawerMenuConfig contains a value for collapsible
            collapsible: customDrawerMenuConfig.collapsible ?? drawerMenuConfig.collapsible,
        };
    }

    // if there is no customDrawerMenuConfig then return the original drawerMenuConfig
    return drawerMenuConfig;
};


/**
 * Given a ritzPageConfig and a customPageConfig, merge the customPageConfig into the
 * ritzPageConfig to override the props in ritzPageConfig.
 * @param ritzPageConfig
 * @param customPageConfig
 */
export const mergeRitzPageConfig = (
    ritzPageConfig: RitzPageConfig,
    customPageConfig: RitzPageConfig | undefined,
): RitzPageConfig => {
    if (customPageConfig) {

        // Return a new RitzPageConfig object by replacing the props from RitzPageConfig with the new props from
        // CustomRitzPageConfig
        return {
            // make a copy of the RitzPageConfig
            ...ritzPageConfig,

            // override the titleKey/subtitleKey/components if the customDashboardConfig contains a value for titleKey/subtitleKey/components
            className  : customPageConfig.className || ritzPageConfig.className,
            style      : customPageConfig.style || ritzPageConfig.style,
            titleKey   : customPageConfig.titleKey || ritzPageConfig.titleKey,
            subtitleKey: customPageConfig.subtitleKey || ritzPageConfig.subtitleKey,
            components : customPageConfig.components || ritzPageConfig.components,
        };
    }

    // if there is no CustomRitzPageConfig then return the original drawerMenuConfig
    return ritzPageConfig;
};


/**
 * Given a UI config for a project based on it's projectType, merge the config with the project's specific custom config if the project
 * has a custom config. Not all project has a custom UI config. If a project does not have a custom UI config, that project will use the
 * config based on the projectType.
 *
 * @param loggedInAgent
 * @param project
 * @param uiConfigByProjectType
 */
export const integrateProjectTypeCustomUIConfigWithProjectCustomConfig = (
    loggedInAgent: Agent | undefined,
    project: Project | undefined,
    uiConfigByProjectType: ProjectUIConfig,
): ProjectUIConfig => {

    if (project) {
        // now get the specific project config from the project.supportToolSettings. This 'getSpecificProjectCustomUIConfig' will handle
        // validating the config to make sure the config is in valid format and return a ProjectCustomUIConfig if it is valid.
        const specificProjectCustomUIConfig = getSpecificProjectCustomUIConfig(project);

        // If there is a custom UI config for the current project then we need to use this custom config to override some of the
        // config from the defaultRCProjectConfig.
        if (specificProjectCustomUIConfig) {

            const mergedDrawerMenuConfig = mergeDrawerMenuConfig(uiConfigByProjectType.drawerMenu, specificProjectCustomUIConfig.drawerMenu);
            const mergedProjectDashboardConfig = mergeRitzPageConfig(
                uiConfigByProjectType.projectDashboardPage,
                specificProjectCustomUIConfig.projectDashboardPage,
            );

            return {
                ...uiConfigByProjectType,
                drawerMenu           : mergedDrawerMenuConfig,
                landingPage          : specificProjectCustomUIConfig.landingPage,
                projectDashboardPage : mergedProjectDashboardConfig,
                enableCompanyView    : specificProjectCustomUIConfig.enableCompanyView ?? uiConfigByProjectType.enableCompanyView,
                enabledFeatures      : specificProjectCustomUIConfig.enabledFeatures ?? uiConfigByProjectType.enabledFeatures,
                entityClassUISettings: specificProjectCustomUIConfig.entityClassUISettings,
            };
        }
    }

    // There is no projectSelected or the project does not have a custom UI config, return the config by project type.
    return uiConfigByProjectType;
};


/**
 * Sometime we need to configure a component with some values that are not known at config time and need to be set dynamically. For example:
 * We want to add a <UserInfoComp /> into the page and this component requires a projectId and userId like this
 * <UserInfoComp projectId={1234} userId={5678} />. We can't specify the projectId or userId in the config because these ID are based on
 * what project/user the user is looking at. So in the config, we have to set the values for these props like this
 * projectId="{{projectId}}" userId="{{userId}}". The {{propName}} is a syntax we will be using in the config to let the system know that the
 * value is not an actual value but only a placeholder and we need to be replace {{propName}} with an actual value.
 *
 * Convert propValueTemplate from something like this '${projectId}' to 1234 if values contains 'projectId': 1234
 * @param propValueTemplate
 * @param values
 */
export const replacePropValueTemplate = (propValueTemplate: string, values: {}): any => {
    // remove the first "{{" and the last "}}" from the template
    const key = propValueTemplate.replace(/^({{)+/, '').replace(/(}})$/, '');
    if (values[key]) {
        return values[key];
    }
    return undefined;
};


/**
 * A helper function that will create a Ritz component based on the config.
 * @param config
 * @param key
 * @param ritzCompParams
 */
export const createRitzComponent = (config: RitzComponentConfig, key: string, ritzCompParams: {}, trans: TFunction) => {
    // Get the component type based on the componentName
    const RitzComponent = RitzComponentsCollection.getInstance().getComponent(config.componentName);
    if (RitzComponent) {
        const requiredProps = Object.keys(config.routeParamProps).reduce((result, propName: string) => {
            const propValue = config.routeParamProps[propName];

            // if propValue is a template string, a string in this format "{{propName}}" then we need to call replacePropValueTemplate
            // to get the actual value for this prop.
            if (typeof propValue === 'string' && /^{{+.+}}$/.test(propValue)) {
                return {
                    ...result,
                    [propName]: replacePropValueTemplate(propValue, ritzCompParams),
                };
            }
            return {
                ...result,
                [propName]: propValue,
            };
        }, {});

        return (
            <RitzComponent
                key={key}
                {...requiredProps}
                {...config.compProps}
            />
        );
    }

    return (
        <Paper key={key}>
            <GenericMessageBox
                icon="report"
                iconColor="error"
                title={trans('error.invalid_component.title', { component_name: config.componentName })}
                message={trans('error.invalid_component.message', { component_name: config.componentName })}
            />
        </Paper>
    );
};



/**
 * A helper function that will create a <Grid item /> and will also recursively create a components for each of the component in the config.components
 * @param config
 * @param key
 * @param ritzCompParams
 */
export const createGridItemComponent = (config: GridItemConfig, key: string, ritzCompParams: {}, trans: TFunction) => {
    return (
        <Grid key={key} item {...config.compProps}>
            {createComponents(config.components, ritzCompParams, trans)}
        </Grid>
    );
};



/**
 * A helper function that will create a <Grid container /> and will also recursively create <Grid items /> for each of the item in
 * the config.gridItems
 * @param config
 * @param key
 * @param ritzCompParams
 */
export const createGridContainerComp = (config: GridContainerConfig, key: string, ritzCompParams: {}, trans: TFunction) => {
    return (
        <Grid key={key} container {...config.compProps}>
            {config.gridItems.map((item, index: number) => {
                return createGridItemComponent(item, `container-${key}-item-${index}`, ritzCompParams, trans);
            })}
        </Grid>
    );
};


/**
 * A helper function that will create components based on the provided config
 * @param componentsConfig
 * @param ritzCompParams
 */
export const createComponents = (componentsConfig: readonly (GridContainerConfig | RitzComponentConfig)[], ritzCompParams: {}, trans: TFunction) => {
    return componentsConfig.map((config: GridContainerConfig | RitzComponentConfig, index: number) => {
        if (isGridContainerConfig(config)) {
            return createGridContainerComp(config, `container-${index}`, ritzCompParams, trans);
        }
        if (isRitzComponentConfig(config)) {
            return createRitzComponent(config, `component-${config.componentName}-${index}`, ritzCompParams, trans);
        }
        return <></>;
    });
};
